import React, { useEffect, useState } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

// Register the necessary components for the chart
ChartJS.register(ArcElement, Tooltip, Legend);

// Define the type for the individual pie category
interface PieCategory {
    label: string;
    value: number;
    baseType?: string;
}

// Define the type for the PieChart component props
interface PieChartProps {
    data: PieCategory[];
    colorMappings?: Record<string, string>;
}

const PieChart: React.FC<PieChartProps> = ({ data, colorMappings }) => {
    const [isVisible, setIsVisible] = useState(false);

    const sortedData = data.sort((a, b) => a.label.localeCompare(b.label));
    const names = sortedData.map(item => item.label);
    const percentages = sortedData.map(item => item.value);

    // Default colors fallback
    const defaultColors = [
        '#FDCA7E',
        '#FF8080',
        '#3BC1EC',
        '#85DF88',
    ];

    // Helper function to lighten a hex color
    const lightenColor = (hex: string, percent: number): string => {
        const num = parseInt(hex.replace('#', ''), 16);
        const r = Math.min(255, ((num >> 16) & 0xFF) + Math.round(255 * percent));
        const g = Math.min(255, ((num >> 8) & 0xFF) + Math.round(255 * percent));
        const b = Math.min(255, (num & 0xFF) + Math.round(255 * percent));
        return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
    };

    // Helper function to darken a hex color
    const darkenColor = (hex: string, percent: number): string => {
        const num = parseInt(hex.replace('#', ''), 16);
        const r = Math.max(0, ((num >> 16) & 0xFF) - Math.round(255 * percent));
        const g = Math.max(0, ((num >> 8) & 0xFF) - Math.round(255 * percent));
        const b = Math.max(0, (num & 0xFF) - Math.round(255 * percent));
        return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
    };

    // Track base type usage to generate variants
    const baseTypeGroups = new Map<string, PieCategory[]>();
    const baseTypeBaseColors = new Map<string, string>();

    // First pass: group items by base type and identify base colors
    sortedData.forEach((item) => {
        if (item.baseType) {
            if (!baseTypeGroups.has(item.baseType)) {
                baseTypeGroups.set(item.baseType, []);
                // Get base color from mappings or use default
                const baseColor = colorMappings && colorMappings[item.baseType]
                    ? colorMappings[item.baseType]
                    : defaultColors[baseTypeGroups.size % defaultColors.length];
                baseTypeBaseColors.set(item.baseType, baseColor);
            }
            baseTypeGroups.get(item.baseType)!.push(item);
        }
    });

    // Second pass: assign colors with variants for same base types
    const colors = sortedData.map((item) => {
        if (item.baseType && baseTypeGroups.has(item.baseType)) {
            const group = baseTypeGroups.get(item.baseType)!;
            const baseColor = baseTypeBaseColors.get(item.baseType)!;
            
            // Find position within the group (sorted by label)
            const groupIndex = group.findIndex(g => g.label === item.label);
            
            // If only one item with this base type, use base color
            if (group.length === 1) {
                return baseColor;
            }
            
            // Generate variants: first gets base, then progressively lighter
            // Variant percentages: 0% (base), 15%, 25%, 35%, etc.
            const variantPercent = groupIndex * 0.15;
            return lightenColor(baseColor, Math.min(variantPercent, 0.5));
        }
        
        // Fallback to default colors if no base type
        const defaultIndex = sortedData.indexOf(item);
        return defaultColors[defaultIndex % defaultColors.length];
    });

    // Define the data structure for the chart
    const chartData = {
        labels: names, // Use the names as labels
        datasets: [
            {
                data: percentages, // Use the percentages for the data values
                backgroundColor: colors, // Assign the colors based on baseType
                hoverBackgroundColor: colors, // Colors when hovering
                borderWidth: 3, // Width of the border (gap size)
                borderColor: '#ffffff', // The color of the gap between segments (white gap)
            },
        ],
    };

    // Chart configuration
    const options = {
        responsive: true,
        animation: false,
        plugins: {
            legend: {
                position: 'bottom' as const, // Legend on the right
                labels: {
                    boxWidth: 20, // Size of the color box in the legend
                    padding: 10, // Add padding between legend items
                },
            },
            tooltip: {
                padding: 8,
                boxPadding: 4,
                backgroundColor: '#2B2C82', // Tooltip background color
                titleColor: '#ffffff', // Title text color
                bodyColor: '#ffffff', // Body text color
                borderColor: 'rgba(255, 255, 255, 0.8)', // Border color
                borderWidth: 1, // Border width
                callbacks: {
                    label: function (tooltipItem: any) {
                        return `${percentages[tooltipItem.dataIndex]}%`;
                    },
                },
            },
            maintainAspectRatio: false, // Allow us to customize chart size
        },

    };

    // Trigger fade-in after component mounts and chart has time to calculate dimensions
    useEffect(() => {
        // Small delay to allow Chart.js to calculate dimensions before fade-in
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, 50);

        return () => clearTimeout(timer);
    }, []);

    return (
        <div className={`pie-chart__wrapper ${isVisible ? 'pie-chart__wrapper--visible' : ''}`}>
            <Pie data={chartData} options={options} />
        </div>
    );
};

export default PieChart;
